import { useQuery } from "@tanstack/react-query";
import type { ProductDto } from "@gaole/shared";
import { api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { useIsDesktop } from "../hooks/useBreakpoint";
import { HomeDesktop } from "../components/home/HomeDesktop";
import { HomeMobile } from "../components/home/HomeMobile";
import type { HomeAuction, HomePack, HomeRaffle } from "../components/home/types";
import type { LeaderboardEntry } from "../components/storefront/LeaderboardTeaser";
/**
 * Fetches once, then hands off to whichever of the two home layouts fits the
 * viewport. Desktop is a bento grid; mobile is a single scrollable column.
 */
export function Home() {
  const { moduleEnabled } = useAuth();
  const isDesktop = useIsDesktop();

  const { data: raffles } = useQuery({
    queryKey: ["raffles"],
    queryFn: () => api<{ items: HomeRaffle[] }>("/raffles"),
    enabled: moduleEnabled("raffles"),
  });
  const { data: packs } = useQuery({
    queryKey: ["packs"],
    queryFn: () => api<{ items: HomePack[] }>("/packs"),
    enabled: moduleEnabled("packs"),
  });
  const { data: products } = useQuery({
    queryKey: ["products", "featured"],
    queryFn: () => api<{ items: ProductDto[] }>("/catalog/products?pageSize=10"),
    enabled: moduleEnabled("ecommerce"),
  });
  const { data: auctions } = useQuery({
    queryKey: ["auctions"],
    queryFn: () => api<{ items: HomeAuction[] }>("/auctions"),
    enabled: moduleEnabled("auctions"),
    refetchInterval: 30_000,
  });
  const { data: leaderboard } = useQuery({
    queryKey: ["pack-leaderboard", "7d"],
    queryFn: () => api<{ entries: LeaderboardEntry[] }>("/packs/leaderboard?period=7d&limit=3"),
    enabled: moduleEnabled("packs"),
  });

  const view = {
    raffles: raffles?.items ?? [],
    packs: packs?.items ?? [],
    products: products?.items ?? [],
    auctions: auctions?.items ?? [],
    leaderboard: leaderboard?.entries ?? [],
  };

  return isDesktop ? <HomeDesktop {...view} /> : <HomeMobile {...view} />;
}
